import { useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    Chip,
    Link as MuiLink,
    Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MapIcon from "@mui/icons-material/Map";
import BusinessIcon from "@mui/icons-material/Business";

interface IPropsClientsTable {
    clients: any[]
}

const ClientsTable = ({ clients }: IPropsClientsTable) => {
    const navigate = useNavigate();

    const columns: { key: string; label: string; minWidth?: number }[] = [
        { key: "CardCode", label: "Código Cliente", minWidth: 120 },
        { key: "CardName", label: "Nombre del cliente", minWidth: 200 },
        { key: "CardFName", label: "Nombre comercial", minWidth: 180 },
        { key: "Address", label: "Dirección", minWidth: 250 },
        { key: "Notes", label: "Giro", minWidth: 150 },
        { key: "BPL_COORDS", label: "Mapa", minWidth: 120 }
    ];

    const renderCell = (colKey: string, value: any, client?: any) => {
        if (colKey === "BPL_COORDS") {
            const lat = client?.BPLLat;
            const lon = client?.BPLLon;
            if (lat && lon) {
                const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lon}`)}`;
                return (
                    <MuiLink href={href} target="_blank" rel="noopener noreferrer" underline="hover">
                        <Button size="small" startIcon={<MapIcon />} variant="text">
                            Ver mapa
                        </Button>
                    </MuiLink>
                );
            }
            return <Typography variant="caption" color="text.secondary">—</Typography>;
        }

        if (value === null || value === undefined) {
            return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        if (Array.isArray(value)) {
            return <Typography variant="body2">{value.join(", ")}</Typography>;
        }
        if (typeof value === "object") {
            return <Typography variant="body2">{JSON.stringify(value)}</Typography>;
        }
        return <Typography variant="body2">{String(value)}</Typography>;
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}>
            <Paper elevation={2}>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <BusinessIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                            Clientes
                        </Typography>
                        <Chip label={clients.length} color="primary" size="small" />
                    </Stack>
                </Box>

                {/* Table */}
                {clients.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            No se encontraron clientes.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.key}
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: "grey.100",
                                                minWidth: col.minWidth,
                                            }}
                                        >
                                            {col.label}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            bgcolor: "grey.100",
                                            minWidth: 120,
                                            textAlign: "center",
                                        }}
                                    >
                                        Acciones
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.map((client: any, idx: number) => {
                                    const key = client.CardCode ?? idx;
                                    return (
                                        <TableRow
                                            key={key}
                                            hover
                                            sx={{
                                                "&:last-child td, &:last-child th": { border: 0 },
                                            }}
                                        >
                                            {columns.map(col => (
                                                <TableCell key={col.key} sx={{ verticalAlign: "top" }}>
                                                    {col.key === "CardName" ? (
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {client[col.key] ?? "Sin nombre"}
                                                        </Typography>
                                                    ) : (
                                                        renderCell(col.key, client[col.key], client)
                                                    )}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ verticalAlign: "top", textAlign: "center" }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => {
                                                        try {
                                                            sessionStorage.setItem("onsix_selected_client", JSON.stringify(client));
                                                        } catch (e) {
                                                            /* ignore */
                                                        }
                                                        if (client.CardCode) navigate(`/on-six/costumer/${client.CardCode}`);
                                                    }}
                                                >
                                                    Ver
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default ClientsTable;